'use client';

import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Product {
  id: string;
  name: string;
  storage: string;
  colors: string;
  colorOptions: string[];
  fullPrice: string;
  upfront: string;
  monthly: string;
  monthlyRaw: number;
  upfrontRaw: number;
  badge?: string;
  image: string;
  alt: string;
}

interface CustomerForm {
  fullName: string;
  phone: string;
  email: string;
  address: string;
  colour: string;
  courier: string;
  paxiCode: string;
  proofOfPayment: File | null;
}

// ─── Product Data ─────────────────────────────────────────────────────────────
const products: Product[] = [
  {
    id: 'iphone-xr',
    name: 'iPhone XR',
    storage: '64GB',
    colors: 'Available in multiple colors',
    colorOptions: ['Black', 'White', 'Red', 'Blue', 'Yellow', 'Coral'],
    fullPrice: 'R4,000',
    upfront: 'R800',
    monthly: '≈ R1,067/mo',
    monthlyRaw: 1067,
    upfrontRaw: 800,
    image: 'https://appleroom.store/assets/images/0821ec1ca65aeda897e7ad4672e8c8d5-1784180656751.jpg',
    alt: 'iPhone XR front view showing colorful display'
  },
  {
    id: 'iphone-11',
    name: 'iPhone 11',
    storage: '128GB',
    colors: 'Black, White, Green, Purple',
    colorOptions: ['Black', 'White', 'Green', 'Purple', 'Red', 'Yellow'],
    fullPrice: 'R5,500',
    upfront: 'R1,100',
    monthly: '≈ R1,467/mo',
    monthlyRaw: 1467,
    upfrontRaw: 1100,
    badge: 'Popular',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_126862a05-1770137380793.png',
    alt: 'iPhone 11 front view with bright display'
  },
  {
    id: 'iphone-11-pro',
    name: 'iPhone 11 Pro',
    storage: '128GB',
    colors: 'Midnight Green, Space Grey',
    colorOptions: ['Midnight Green', 'Space Grey', 'Silver', 'Gold'],
    fullPrice: 'R6,300',
    upfront: 'R1,260',
    monthly: '≈ R1,680/mo',
    monthlyRaw: 1680,
    upfrontRaw: 1260,
    image: 'https://images.unsplash.com/photo-1610284120508-2bc1905ec669',
    alt: 'iPhone 11 Pro front view in midnight green'
  },
  {
    id: 'iphone-13',
    name: 'iPhone 13',
    storage: '128GB',
    colors: 'Midnight, Starlight, Pink, Blue',
    colorOptions: ['Midnight', 'Starlight', 'Pink', 'Blue', 'Green', 'Red'],
    fullPrice: 'R7,000',
    upfront: 'R1,400',
    monthly: '≈ R1,867/mo',
    monthlyRaw: 1867,
    upfrontRaw: 1400,
    badge: 'Best Value',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c95f6d14-1775058099616.png',
    alt: 'iPhone 13 front view in midnight color with notch'
  },
  {
    id: 'iphone-13-pro-max',
    name: 'iPhone 13 Pro Max',
    storage: '128GB',
    colors: 'Sierra Blue, Gold, Graphite',
    colorOptions: ['Sierra Blue', 'Gold', 'Graphite', 'Silver'],
    fullPrice: 'R8,000',
    upfront: 'R1,600',
    monthly: '≈ R2,133/mo',
    monthlyRaw: 2133,
    upfrontRaw: 1600,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_171571d0d-1772455314835.png',
    alt: 'iPhone 13 Pro Max front view in sierra blue'
  },
  {
    id: 'iphone-14-pro',
    name: 'iPhone 14 Pro',
    storage: '128GB',
    colors: 'Deep Purple, Space Black, Silver',
    colorOptions: ['Deep Purple', 'Space Black', 'Silver', 'Gold'],
    fullPrice: 'R9,300',
    upfront: 'R1,860',
    monthly: '≈ R2,480/mo',
    monthlyRaw: 2480,
    upfrontRaw: 1860,
    badge: 'Dynamic Island',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17e26b727-1772158955332.png',
    alt: 'iPhone 14 Pro front view in deep purple with Dynamic Island'
  },
  {
    id: 'iphone-15-pro-max',
    name: 'iPhone 15 Pro Max',
    storage: '128GB',
    colors: 'Black Titanium, Natural Titanium',
    colorOptions: ['Black Titanium', 'Natural Titanium', 'White Titanium', 'Blue Titanium'],
    fullPrice: 'R10,500',
    upfront: 'R2,100',
    monthly: '≈ R2,800/mo',
    monthlyRaw: 2800,
    upfrontRaw: 2100,
    badge: 'Titanium',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17b82fb7a-1772960574407.png',
    alt: 'iPhone 15 Pro Max front view in black titanium finish'
  },
  {
    id: 'iphone-16',
    name: 'iPhone 16',
    storage: '256GB',
    colors: 'Ultramarine, Teal, Pink, White, Black',
    colorOptions: ['Ultramarine', 'Teal', 'Pink', 'White', 'Black'],
    fullPrice: 'R13,000',
    upfront: 'R2,600',
    monthly: '≈ R3,467/mo',
    monthlyRaw: 3467,
    upfrontRaw: 2600,
    badge: 'New',
    image: 'https://appleroom.store/assets/images/ffbf63691a5eb79d217105e82c648981-1784181076428.jpg',
    alt: 'iPhone 16 front view in ultramarine with camera control button'
  },
  {
    id: 'iphone-17',
    name: 'iPhone 17',
    storage: '256GB',
    colors: 'Desert Titanium, Sky Blue, Black',
    colorOptions: ['Desert Titanium', 'Sky Blue', 'Black'],
    fullPrice: 'R15,000',
    upfront: 'R3,000',
    monthly: '≈ R4,000/mo',
    monthlyRaw: 4000,
    upfrontRaw: 3000,
    badge: 'Latest 2025',
    image: 'https://appleroom.store/assets/images/79ea807900a1cdc58aded73674714a96-1784181175057.jpg',
    alt: 'iPhone 17 front view in desert titanium with latest design'
  }
];

// ─── ADMIN CONFIG — paste your courier tracking URL here ─────────────────────
const TRACKING_URL = '';

// ─── ADMIN CONFIG — paste each courier's tracking URL here ───────────────────
const COURIER_TRACKING_URLS: Record<string, string> = {
  paxi: '',          // e.g. https://paxi.co.za/track/XXXXXXX
  courierguy: '',    // e.g. https://www.thecourierguy.co.za/tracking
  takealot: '',      // e.g. https://www.takealot.com/account/orders
};

// ─── Courier Options ──────────────────────────────────────────────────────────
const courierOptions = [
  {
    id: 'paxi',
    name: 'Pep / Paxi',
    description: 'Collect at your nearest Pep store',
    eta: '3 days',
    icon: '🏪',
    requiresCode: true,
    codePlaceholder: 'Enter your Paxi point code (e.g. PEP1234)',
    codeLabel: 'Paxi Point Code',
  },
  {
    id: 'courierguy',
    name: 'Courier Guy',
    description: 'Delivered to your address above',
    eta: '5 days',
    icon: '🚚',
    requiresCode: false,
  },
  {
    id: 'takealot',
    name: 'Takealot',
    description: 'Delivered to your address above',
    eta: '3 days',
    icon: '📦',
    requiresCode: false,
  },
];

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PackageIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TruckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UploadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

// ─── Order Confirmation Content ───────────────────────────────────────────────
function OrderConfirmationContent() {
  const searchParams = useSearchParams();
  const productId = searchParams.get('product') || 'iphone-11';
  const orderRef = searchParams.get('ref') || generateOrderRef();

  const product = products.find((p) => p.id === productId) || products[1];

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<CustomerForm>({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    colour: '',
    courier: '',
    paxiCode: '',
    proofOfPayment: null,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CustomerForm, string>>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, proofOfPayment: file }));
    setErrors((prev) => ({ ...prev, proofOfPayment: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CustomerForm, string>> = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.email.trim()) newErrors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Enter a valid email address';
    if (!form.address.trim()) newErrors.address = 'Delivery address is required';
    if (!form.colour) newErrors.colour = 'Please choose an iPhone colour';
    if (!form.courier) newErrors.courier = 'Please select a courier option';
    if (form.courier === 'paxi' && !form.paxiCode.trim()) newErrors.paxiCode = 'Please enter your Paxi point code';
    if (!form.proofOfPayment) newErrors.proofOfPayment = 'Please upload proof of payment';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitted(true);
    }
  };

  // Monthly payment schedule
  const paymentSchedule = [
    { month: 'Month 1', amount: `R${product.monthlyRaw.toLocaleString()}`, status: 'upcoming' },
    { month: 'Month 2', amount: `R${product.monthlyRaw.toLocaleString()}`, status: 'upcoming' },
    { month: 'Month 3', amount: `R${product.monthlyRaw.toLocaleString()}`, status: 'upcoming' },
  ];

  const totalOutstanding = product.monthlyRaw * 3;

  const inputBase = "w-full rounded-xl px-4 py-3 text-sm font-medium outline-none transition-all duration-200 focus:ring-2";
  const inputStyle = {
    backgroundColor: 'rgba(42,42,42,0.5)',
    border: '1px solid var(--border)',
    color: 'var(--foreground)',
  };

  return (
    <main className="min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--background)' }}>
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: '#ffffff', borderBottomColor: 'rgba(113, 63, 18, 0.4)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2.5">
              <Image
                src="/assets/images/IMG_20260716_072624-1785031428263.png"
                alt="AppleRoom logo"
                width={40}
                height={40}
                className="rounded-lg object-cover flex-shrink-0"
                style={{ width: '40px', height: '40px' }}
              />
              <span className="font-sans tracking-tight text-xl font-extrabold" style={{ color: '#B8860B' }}>
                AppleRoom
              </span>
            </Link>
            <Link
              href="/"
              className="text-sm font-medium transition-colors hover:text-yellow-700"
              style={{ color: '#666' }}
            >
              ← Back to Store
            </Link>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">

          {/* Success Banner */}
          <div
            className="rounded-2xl p-5 mb-8 flex items-start gap-4"
            style={{
              background: 'linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(184,134,11,0.08) 100%)',
              border: '1px solid rgba(201, 168, 76, 0.35)'
            }}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(201, 168, 76, 0.2)', color: '#C9A84C' }}
            >
              <CheckIcon />
            </div>
            <div>
              <h1 className="text-xl font-extrabold mb-1" style={{ color: 'var(--foreground)' }}>
                Order Confirmed! 🎉
              </h1>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Thank you for your order. Your iPhone is being prepared for dispatch.
              </p>
              <p className="text-xs font-bold mt-1" style={{ color: '#C9A84C' }}>
                Order Ref: #{orderRef}
              </p>
            </div>
          </div>

          {/* Product Card */}
          <section
            className="rounded-2xl overflow-hidden mb-6"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="px-5 py-4 flex items-center gap-3 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div style={{ color: '#C9A84C' }}>
                <PackageIcon />
              </div>
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                Your Product
              </h2>
              {product.badge && (
                <span
                  className="ml-auto text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: 'rgba(201, 168, 76, 0.15)',
                    color: '#C9A84C',
                    border: '1px solid rgba(201, 168, 76, 0.3)'
                  }}
                >
                  {product.badge}
                </span>
              )}
            </div>

            <div className="p-5 flex gap-5">
              <div
                className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0"
                style={{ border: '1px solid var(--border)' }}
              >
                <img
                  src={product.image}
                  alt={product.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-extrabold mb-0.5" style={{ color: 'var(--foreground)' }}>
                  {product.name}
                </h3>
                <p className="text-sm mb-3" style={{ color: 'var(--muted-foreground)' }}>
                  {product.storage} · {product.colors}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className="rounded-xl p-2.5 text-center"
                    style={{ backgroundColor: 'rgba(42,42,42,0.5)', border: '1px solid var(--border)' }}
                  >
                    <div className="text-xs mb-0.5" style={{ color: 'var(--muted-foreground)' }}>Full Price</div>
                    <div className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{product.fullPrice}</div>
                  </div>
                  <div
                    className="rounded-xl p-2.5 text-center"
                    style={{ backgroundColor: 'rgba(201, 168, 76, 0.08)', border: '1px solid rgba(201, 168, 76, 0.25)' }}
                  >
                    <div className="text-xs mb-0.5" style={{ color: 'var(--muted-foreground)' }}>Paid Upfront</div>
                    <div className="text-sm font-bold" style={{ color: '#C9A84C' }}>{product.upfront}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Outstanding Monthly Payments */}
          <section
            className="rounded-2xl overflow-hidden mb-6"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="px-5 py-4 flex items-center gap-3 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div style={{ color: '#C9A84C' }}>
                <CalendarIcon />
              </div>
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                Outstanding Monthly Payments
              </h2>
            </div>

            <div className="p-5">
              <div className="space-y-3 mb-4">
                {paymentSchedule.map((payment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-xl px-4 py-3"
                    style={{
                      backgroundColor: 'rgba(42,42,42,0.4)',
                      border: '1px solid var(--border)'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{
                          backgroundColor: 'rgba(201, 168, 76, 0.12)',
                          color: '#C9A84C',
                          border: '1px solid rgba(201, 168, 76, 0.25)'
                        }}
                      >
                        {index + 1}
                      </div>
                      <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                        {payment.month}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                        {payment.amount}
                      </span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: 'rgba(251, 191, 36, 0.1)',
                          color: '#FBBF24',
                          border: '1px solid rgba(251, 191, 36, 0.2)'
                        }}
                      >
                        Upcoming
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total Outstanding */}
              <div
                className="flex items-center justify-between rounded-xl px-4 py-3"
                style={{
                  background: 'linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(184,134,11,0.06) 100%)',
                  border: '1px solid rgba(201, 168, 76, 0.3)'
                }}
              >
                <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                  Total Outstanding Balance
                </span>
                <span className="text-base font-extrabold" style={{ color: '#C9A84C' }}>
                  R{totalOutstanding.toLocaleString()}
                </span>
              </div>

              <p className="text-xs mt-3 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Payments are due monthly after receiving your device. You will be contacted via SMS/WhatsApp with payment reminders.
              </p>
            </div>
          </section>

          {/* ─── Customer Details Form ─────────────────────────────────────── */}
          <section
            className="rounded-2xl overflow-hidden mb-6"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="px-5 py-4 flex items-center gap-3 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div style={{ color: '#C9A84C' }}>
                <UserIcon />
              </div>
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                Customer Details
              </h2>
            </div>

            {submitted ? (
              <div className="p-6 text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(201, 168, 76, 0.15)', color: '#C9A84C' }}
                >
                  <CheckIcon />
                </div>
                <h3 className="text-base font-extrabold mb-1" style={{ color: 'var(--foreground)' }}>
                  Details Submitted!
                </h3>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Your order details and proof of payment have been received. We will contact you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Full Name <span style={{ color: '#C9A84C' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={inputBase}
                    style={{
                      ...inputStyle,
                      boxShadow: errors.fullName ? '0 0 0 2px rgba(239,68,68,0.4)' : undefined,
                    }}
                  />
                  {errors.fullName && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.fullName}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Phone Number <span style={{ color: '#C9A84C' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g. 071 234 5678"
                    className={inputBase}
                    style={{
                      ...inputStyle,
                      boxShadow: errors.phone ? '0 0 0 2px rgba(239,68,68,0.4)' : undefined,
                    }}
                  />
                  {errors.phone && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.phone}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Email Address <span style={{ color: '#C9A84C' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className={inputBase}
                    style={{
                      ...inputStyle,
                      boxShadow: errors.email ? '0 0 0 2px rgba(239,68,68,0.4)' : undefined,
                    }}
                  />
                  {errors.email && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.email}</p>
                  )}
                </div>

                {/* Delivery Address */}
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Delivery Address <span style={{ color: '#C9A84C' }}>*</span>
                  </label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Street, City, Province, Postal Code"
                    rows={3}
                    className={inputBase + ' resize-none'}
                    style={{
                      ...inputStyle,
                      boxShadow: errors.address ? '0 0 0 2px rgba(239,68,68,0.4)' : undefined,
                    }}
                  />
                  {errors.address && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.address}</p>
                  )}
                </div>

                {/* Choose iPhone Colour */}
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Choose iPhone Colour <span style={{ color: '#C9A84C' }}>*</span>
                  </label>
                  <select
                    name="colour"
                    value={form.colour}
                    onChange={handleChange}
                    className={inputBase + ' cursor-pointer'}
                    style={{
                      ...inputStyle,
                      boxShadow: errors.colour ? '0 0 0 2px rgba(239,68,68,0.4)' : undefined,
                    }}
                  >
                    <option value="" disabled style={{ backgroundColor: '#1a1a1a' }}>
                      Select a colour
                    </option>
                    {product.colorOptions.map((colour) => (
                      <option key={colour} value={colour} style={{ backgroundColor: '#1a1a1a' }}>
                        {colour}
                      </option>
                    ))}
                  </select>
                  {errors.colour && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.colour}</p>
                  )}
                </div>

                {/* ─── Courier Selection ─────────────────────────────────── */}
                <div>
                  <label className="block text-xs font-bold mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    Choose Courier <span style={{ color: '#C9A84C' }}>*</span>
                  </label>
                  <div className="space-y-2.5">
                    {courierOptions.map((option) => {
                      const isSelected = form.courier === option.id;
                      return (
                        <div key={option.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, courier: option.id, paxiCode: '' }));
                              setErrors((prev) => ({ ...prev, courier: '', paxiCode: '' }));
                            }}
                            className="w-full text-left rounded-xl px-4 py-3 transition-all duration-200 flex items-center gap-3"
                            style={{
                              backgroundColor: isSelected ? 'rgba(201, 168, 76, 0.1)' : 'rgba(42,42,42,0.4)',
                              border: isSelected
                                ? '1.5px solid rgba(201, 168, 76, 0.6)'
                                : '1px solid var(--border)',
                              boxShadow: isSelected ? '0 0 0 1px rgba(201,168,76,0.15)' : undefined,
                            }}
                          >
                            {/* Radio circle */}
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center"
                              style={{
                                border: isSelected ? '2px solid #C9A84C' : '2px solid rgba(150,150,150,0.5)',
                                backgroundColor: isSelected ? 'rgba(201,168,76,0.15)' : 'transparent',
                              }}
                            >
                              {isSelected && (
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: '#C9A84C' }}
                                />
                              )}
                            </div>
                            <span className="text-lg flex-shrink-0">{option.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                                  {option.name}
                                </span>
                                <span
                                  className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                                  style={{
                                    backgroundColor: 'rgba(201, 168, 76, 0.12)',
                                    color: '#C9A84C',
                                    border: '1px solid rgba(201, 168, 76, 0.25)',
                                  }}
                                >
                                  {option.eta}
                                </span>
                              </div>
                              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                                {option.description}
                              </p>
                            </div>
                          </button>

                          {/* Paxi point code field */}
                          {isSelected && option.requiresCode && (
                            <div className="mt-2 ml-7">
                              <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                                {option.codeLabel} <span style={{ color: '#C9A84C' }}>*</span>
                              </label>
                              <input
                                type="text"
                                name="paxiCode"
                                value={form.paxiCode}
                                onChange={handleChange}
                                placeholder={option.codePlaceholder}
                                className={inputBase}
                                style={{
                                  ...inputStyle,
                                  boxShadow: errors.paxiCode ? '0 0 0 2px rgba(239,68,68,0.4)' : undefined,
                                }}
                              />
                              {errors.paxiCode && (
                                <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.paxiCode}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {errors.courier && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.courier}</p>
                  )}
                </div>
                {/* ─── End Courier Selection ─────────────────────────────── */}

                {/* Upload Proof of Payment */}
                <div>
                  <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Upload Proof of Payment <span style={{ color: '#C9A84C' }}>*</span>
                  </label>

                  {/* TymeBank reminder */}
                  <div className="flex items-center gap-3 mb-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(201, 168, 76, 0.08)', border: '1px solid rgba(201, 168, 76, 0.2)' }}>
                    <img
                      src="/assets/images/images__53_-1785033298892.jpeg"
                      alt="TymeBank Logo"
                      className="w-8 h-8 object-contain rounded-md flex-shrink-0"
                    />
                    <p className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>
                      PAY YOUR 20% AT TYMEBANK · Appleroom Account 51129601294
                    </p>
                  </div>

                  <div
                    className="rounded-xl border-2 border-dashed p-5 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-200 hover:border-yellow-600"
                    style={{
                      borderColor: errors.proofOfPayment ? '#EF4444' : 'rgba(201, 168, 76, 0.4)',
                      backgroundColor: 'rgba(42,42,42,0.3)',
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div style={{ color: '#C9A84C' }}>
                      <UploadIcon />
                    </div>
                    {form.proofOfPayment ? (
                      <p className="text-sm font-semibold text-center" style={{ color: '#C9A84C' }}>
                        ✓ {form.proofOfPayment.name}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                          Click to upload proof of payment
                        </p>
                        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          PNG, JPG, PDF accepted
                        </p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  {errors.proofOfPayment && (
                    <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.proofOfPayment}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl text-sm font-extrabold flex items-center justify-center gap-2 transition-all duration-200 mt-2"
                  style={{
                    background: 'linear-gradient(135deg, #C9A84C 0%, #B8860B 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 16px rgba(201,168,76,0.3)',
                  }}
                >
                  <CheckIcon />
                  Submit Order Details
                </button>
              </form>
            )}
          </section>

          {/* Order Summary */}
          <section
            className="rounded-2xl overflow-hidden mb-6"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="px-5 py-4 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                Order Summary
              </h2>
            </div>
            <div className="p-5 space-y-3">
              {[
                { label: 'Order Reference', value: `#${orderRef}` },
                { label: 'Product', value: `${product.name} ${product.storage}` },
                { label: 'Upfront Paid (20%)', value: product.upfront },
                { label: 'Remaining Balance (80%)', value: `R${totalOutstanding.toLocaleString()}` },
                { label: 'Installments', value: `3 × ${product.monthly.replace('≈ ', '')}` },
                { label: 'Delivery', value: 'Same-day courier dispatch' },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                  style={{ borderColor: 'rgba(42,42,42,0.5)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{label}</span>
                  <span className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>{value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* TymeBank Payment Details Section */}
          <section
            className="rounded-2xl overflow-hidden mb-6"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="px-5 py-4 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                TymeBank Payment Details
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 text-sm font-medium" style={{ color: 'var(--foreground)' }}>Account Number</div>
                <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>1234 5678 9012 3456</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 text-sm font-medium" style={{ color: 'var(--foreground)' }}>Sort Code</div>
                <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>12-34-56</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 text-sm font-medium" style={{ color: 'var(--foreground)' }}>Expiry Month</div>
                <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>01/25</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 text-sm font-medium" style={{ color: 'var(--foreground)' }}>CVV</div>
                <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>123</div>
              </div>
            </div>
          </section>

          {/* TymeBank Payment Details */}
          <div className="flex items-center gap-3 mb-6 p-4 rounded-2xl" style={{ backgroundColor: 'rgba(201, 168, 76, 0.08)', border: '1px solid rgba(201, 168, 76, 0.2)' }}>
            <img
              src="/assets/images/images__53_-1785033298892.jpeg"
              alt="TymeBank Logo"
              className="w-10 h-10 object-contain rounded-md flex-shrink-0"
            />
            <p className="text-xs sm:text-sm font-bold" style={{ color: 'var(--foreground)' }}>
              PAY YOUR 20% AT TYMEBANK Appleroom Account 51129601294 (upload proof of payment below)
            </p>
          </div>

          {/* Track Order Section */}
          <section
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div
              className="px-5 py-4 flex items-center gap-3 border-b"
              style={{ borderColor: 'var(--border)' }}
            >
              <div style={{ color: '#C9A84C' }}>
                <TruckIcon />
              </div>
              <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>
                Track Your Order
              </h2>
            </div>

            <div className="p-5">
              {/* SMS notice */}
              <div
                className="rounded-xl px-4 py-3 mb-5 flex items-start gap-3"
                style={{
                  backgroundColor: 'rgba(201, 168, 76, 0.06)',
                  border: '1px solid rgba(201, 168, 76, 0.2)'
                }}
              >
                <span className="text-lg flex-shrink-0 mt-0.5">📱</span>
                <p className="text-sm font-semibold leading-relaxed" style={{ color: 'var(--foreground)' }}>
                  Wait for a confirmation sms to start tracking your order when it has been dropped off.
                </p>
              </div>

              {/* Track Order Button — uses selected courier's URL, falls back to global TRACKING_URL */}
              {(() => {
                const activeCourierUrl = form.courier ? COURIER_TRACKING_URLS[form.courier] : '';
                const activeUrl = activeCourierUrl || TRACKING_URL;
                const courierLabel = form.courier
                  ? courierOptions.find((c) => c.id === form.courier)?.name || 'My Order' :'My Order';
                return activeUrl ? (
                  <a
                    href={activeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all duration-200 btn-gold"
                  >
                    <TruckIcon />
                    Track {courierLabel} Order
                    <ArrowIcon />
                  </a>
                ) : (
                  <button
                    disabled
                    className="w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 cursor-not-allowed opacity-50"
                    style={{
                      backgroundColor: 'rgba(201, 168, 76, 0.15)',
                      border: '1px solid rgba(201, 168, 76, 0.3)',
                      color: '#C9A84C'
                    }}
                  >
                    <TruckIcon />
                    Track My Order
                    <ArrowIcon />
                  </button>
                );
              })()}

              {!TRACKING_URL && !(form.courier && COURIER_TRACKING_URLS[form.courier]) && (
                <p className="text-xs text-center mt-2" style={{ color: 'var(--muted-foreground)' }}>
                  Tracking link will be activated once your order is dispatched.
                </p>
              )}
            </div>
          </section>

          {/* Back to store */}
          <div className="text-center mt-8">
            <Link
              href="/"
              className="text-sm font-medium transition-colors hover:text-yellow-600"
              style={{ color: 'var(--muted-foreground)' }}
            >
              ← Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────
function generateOrderRef(): string {
  return 'AR' + Math.floor(100000 + Math.random() * 900000).toString();
}

// ─── Page Export with Suspense ────────────────────────────────────────────────
export default function OrderConfirmationPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3" style={{ borderColor: '#C9A84C', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading your order...</p>
        </div>
      </div>
    }>
      <OrderConfirmationContent />
    </Suspense>
  );
}
